import { useAssets } from '../../hooks/useAssets';
import { useDividends } from '../../hooks/useDividends';
import type { Dividend } from '../../types';
import { hideCurrency, hidePercent, gainLossClass } from '../../utils/format';
import { useValuesVisibility } from '../../stores/valuesVisibilityStore';
import { PageLoader } from '../../components/common/LoadingSpinner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export default function FundsPage() {
  const { data: fiis, isLoading: fiisLoading } = useAssets({ type: 'FII' });
  const { data: funds, isLoading: fundsLoading } = useAssets({ type: 'FUND' });
  const { isVisible } = useValuesVisibility();
  const { data: dividends } = useDividends(new Date().getFullYear());

  const allFunds = [...(fiis || []), ...(funds || [])];

  const isLoading = fiisLoading || fundsLoading;

  // Calculate portfolio values
  const portfolioData = allFunds.map((asset) => {
    const totalInvested = asset.quantity * asset.averagePrice;
    const currentValue = asset.quantity * (asset.currentPrice ?? asset.averagePrice);
    const gainLoss = currentValue - totalInvested;
    const gainLossPercent = totalInvested > 0 ? (gainLoss / totalInvested) * 100 : 0;

    // Get dividends for this asset
    const assetDividends = dividends?.dividends?.filter(
      (d: Dividend) => d.assetId === asset.id,
    ) || [];
    const totalDividendsReceived = assetDividends.reduce(
      (s: number, d: Dividend) => s + Number(d.totalValue),
      0,
    );

    return {
      ...asset,
      totalInvested,
      currentValue,
      gainLoss,
      gainLossPercent,
      totalDividendsReceived,
      yieldOnCost:
        totalInvested > 0 ? (totalDividendsReceived / totalInvested) * 100 : 0,
    };
  });

  // Monthly dividends for chart
  const monthlyDividendsData = Array.from({ length: 12 }, (_, i) => {
    const month = new Date(new Date().getFullYear(), i, 1);
    const monthKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
    const monthDivs = dividends?.dividends?.filter(
      (d: Dividend) => d.paymentDate?.startsWith(monthKey),
    ) || [];
    return {
      month: month.toLocaleDateString('pt-BR', { month: 'short' }),
      total: monthDivs.reduce((s: number, d: Dividend) => s + Number(d.totalValue), 0),
    };
  });

  const totalInvested = portfolioData.reduce((s, a) => s + a.totalInvested, 0);
  const totalCurrentValue = portfolioData.reduce((s, a) => s + a.currentValue, 0);
  const totalDividends = portfolioData.reduce((s, a) => s + a.totalDividendsReceived, 0);

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Fundos & FIIs</h1>
        <p className="page-subtitle">FIIs, fundos de investimento e rendimentos</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card">
          <span className="stat-label">Total Investido</span>
          <span className="stat-value text-white">{hideCurrency(totalInvested, isVisible)}</span>
          <span className="text-xs text-white/40">{allFunds.length} ativos</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Valor Atual</span>
          <span className="stat-value text-white">{hideCurrency(totalCurrentValue, isVisible)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Proventos Recebidos</span>
          <span className="stat-value text-success-500">{hideCurrency(totalDividends, isVisible)}</span>
          <span className="text-xs text-white/40">No ano</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Yield on Cost</span>
          <span className="stat-value text-primary-400">
            {totalInvested > 0 ? hidePercent((totalDividends / totalInvested) * 100, isVisible) : '0%'}
          </span>
          <span className="text-xs text-white/40">Rendimento sobre custo</span>
        </div>
      </div>

      {/* Monthly Dividends Chart */}
      <div className="card">
        <h2 className="text-sm font-semibold text-white mb-4">Proventos Mensais</h2>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={monthlyDividendsData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }}
              tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload?.length) {
                  return (
                    <div className="bg-surface-800 border border-white/10 rounded-lg p-3 text-xs">
                      <p className="font-medium text-white">{payload[0].payload.month}</p>
                      <p className="text-success-500">{hideCurrency(payload[0].value as number, isVisible)}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="total" fill="#10b981" radius={[4, 4, 0, 0]} opacity={0.8} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Assets Table */}
      {portfolioData.length > 0 ? (
        <div className="card">
          <h2 className="text-sm font-semibold text-white mb-4">Fundos & FIIs em Carteira</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Ativo</th>
                  <th>Tipo</th>
                  <th className="text-right">Qtd.</th>
                  <th className="text-right">PM</th>
                  <th className="text-right">Invest.</th>
                  <th className="text-right">Valor Atual</th>
                  <th className="text-right">Lucro/Prej.</th>
                  <th className="text-right">Proventos</th>
                  <th className="text-right">YoC</th>
                </tr>
              </thead>
              <tbody>
                {portfolioData.map((asset) => (
                  <tr key={asset.id}>
                    <td>
                      <p className="font-mono font-medium text-white">{asset.code}</p>
                      <p className="text-xs text-white/40">{asset.name}</p>
                    </td>
                    <td>
                      <span className={`badge text-xs ${asset.type === 'FII' ? 'badge-green' : 'badge-blue'}`}>
                        {asset.type}
                      </span>
                    </td>
                    <td className="text-right font-mono text-sm">{asset.quantity}</td>
                    <td className="text-right font-mono text-sm">{hideCurrency(asset.averagePrice, isVisible)}</td>
                    <td className="text-right font-mono text-sm">{hideCurrency(asset.totalInvested, isVisible)}</td>
                    <td className="text-right font-mono text-sm">{hideCurrency(asset.currentValue, isVisible)}</td>
                    <td className={`text-right font-mono text-sm ${gainLossClass(asset.gainLoss)}`}>
                      <div>
                        <p>{hideCurrency(asset.gainLoss, isVisible)}</p>
                        <p className="text-xs">{hidePercent(asset.gainLossPercent, isVisible)}</p>
                      </div>
                    </td>
                    <td className="text-right font-mono text-sm text-success-500">
                      {hideCurrency(asset.totalDividendsReceived, isVisible)}
                    </td>
                    <td className="text-right font-mono text-sm text-primary-400">
                      {hidePercent(asset.yieldOnCost, isVisible)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card text-center py-12">
          <p className="text-white/30 text-sm">
            Nenhum FII ou fundo de investimento cadastrado.
          </p>
        </div>
      )}
    </div>
  );
}
